import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ISupplierResponseSingleState } from "../types/supplier-response-single";
import {
  getSupplierResponseProductsThunk,
  getSupplierResponseScreenTimeStatsThunk,
  getSupplierResponseCategoriesThunk,
  getItemCommentsThunk,
  getSupplierItemAnswersThunk,
  getProductConflictsThunk,
  createOrUpdateResolutionThunk,
} from "../thunks/supplierResponseSingleThunk";

const initialState: ISupplierResponseSingleState = {
  productsResponse: null,
  activeProductId: null,
  screenTimeStats: null,
  categories: null,
  itemComments: {},
  itemCommentsLoading: {},
  itemCommentsError: {},
  supplierItemAnswers: null,
  supplierItemAnswersLoading: false,
  supplierItemAnswersError: null,
  currentSupplierCarouselIndex: 0,
  loading: false,
  screenTimeStatsLoading: false,
  categoriesLoading: false,
  error: null,
  screenTimeStatsError: null,
  categoriesError: null,
  categoryCollapse: {},
  productConflicts: null,
  productConflictsLoading: false,
  productConflictsError: null,
  selectedConflictItemId: null,
  resolutionLoading: false,
  resolutionError: null,
  submittedConflictItemIds: [], // Submit edilen başarılı conflict item id'leri
};

export const supplierResponseSingleSlice = createSlice({
  name: "supplierResponseSingle",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setActiveProductId: (state, action) => {
      state.activeProductId = action.payload;
    },
    setCurrentSupplierCarouselIndex: (state, action) => {
      state.currentSupplierCarouselIndex = action.payload;
    },
    toggleCategory: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.categoryCollapse[id] = !state.categoryCollapse[id];
    },
    collapseAll: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        state.categoryCollapse[id] = true;
      });
    },
    expandAll: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        state.categoryCollapse[id] = false;
      });
    },
    toggleAll: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload;
      const allCollapsed = ids.every(
        (id) => state.categoryCollapse[id] === true
      );
      ids.forEach((id) => {
        state.categoryCollapse[id] = !allCollapsed;
      });
    },
    setSelectedConflictItemId: (state, action: PayloadAction<string | null>) => {
      state.selectedConflictItemId = action.payload;
    },
    resetConflictData: (state) => {
      state.productConflicts = null;
      state.productConflictsLoading = false;
      state.productConflictsError = null;
      state.selectedConflictItemId = null;
      state.resolutionLoading = false;
      state.resolutionError = null;
      state.submittedConflictItemIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierResponseProductsThunk.pending, (state, action) => {
        // Eğer silent parametresi true ise loading state'i set etme
        if (!action.meta.arg.silent) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getSupplierResponseProductsThunk.fulfilled, (state, action) => {
        // Eğer silent parametresi true ise loading state'i set etme
        if (!action.meta.arg.silent) {
          state.loading = false;
        }
        state.productsResponse = action.payload;
      })
      .addCase(getSupplierResponseProductsThunk.rejected, (state, action) => {
        // Eğer silent parametresi true ise loading state'i set etme
        if (!action.meta.arg.silent) {
          state.loading = false;
        }
        state.error = (action.payload as string) || "Bir hata oluştu";
      })
      .addCase(getSupplierResponseScreenTimeStatsThunk.pending, (state) => {
        state.screenTimeStatsLoading = true;
        state.screenTimeStatsError = null;
      })
      .addCase(
        getSupplierResponseScreenTimeStatsThunk.fulfilled,
        (state, action) => {
          state.screenTimeStatsLoading = false;
          state.screenTimeStats = action.payload;
        }
      )
      .addCase(
        getSupplierResponseScreenTimeStatsThunk.rejected,
        (state, action) => {
          state.screenTimeStatsLoading = false;
          state.screenTimeStatsError =
            (action.payload as string) || "Bir hata oluştu";
        }
      )
      .addCase(getSupplierResponseCategoriesThunk.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(
        getSupplierResponseCategoriesThunk.fulfilled,
        (state, action) => {
          state.categoriesLoading = false;
          state.categories = action.payload;
        }
      )
      .addCase(getSupplierResponseCategoriesThunk.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = (action.payload as string) || "Bir hata oluştu";
      })
      .addCase(getItemCommentsThunk.pending, (state, action) => {
        const key = action.meta.arg.itemId + "-" + action.meta.arg.productId;
        state.itemCommentsLoading[key] = true;
        state.itemCommentsError[key] = null;
      })
      .addCase(getItemCommentsThunk.fulfilled, (state, action) => {
        const key = action.payload.key;
        state.itemCommentsLoading[key] = false;
        state.itemComments[key] = action.payload.data;
      })
      .addCase(getItemCommentsThunk.rejected, (state, action) => {
        const key = action.meta.arg.itemId + "-" + action.meta.arg.productId;
        state.itemCommentsLoading[key] = false;
        state.itemCommentsError[key] =
          (action.payload as string) || "Bir hata oluştu";
      })
      .addCase(getSupplierItemAnswersThunk.pending, (state) => {
        state.supplierItemAnswersLoading = true;
        state.supplierItemAnswersError = null;
      })
      .addCase(getSupplierItemAnswersThunk.fulfilled, (state, action) => {
        state.supplierItemAnswersLoading = false;
        state.supplierItemAnswers = action.payload;
        state.supplierItemAnswersError = null;
      })
      .addCase(getSupplierItemAnswersThunk.rejected, (state, action) => {
        state.supplierItemAnswersLoading = false;
        state.supplierItemAnswersError =
          (action.payload as string) || "Bir hata oluştu";
      })
      .addCase(getProductConflictsThunk.pending, (state) => {
        state.productConflictsLoading = true;
        state.productConflictsError = null;
      })
      .addCase(getProductConflictsThunk.fulfilled, (state, action) => {
        state.productConflictsLoading = false;
        state.productConflicts = action.payload;
        state.productConflictsError = null;
        // Yeni conflicts geldiğinde submitted item id'lerini temizle
        // Çünkü farklı product için submit edilen item'lar farklı olacak
        state.submittedConflictItemIds = [];
        // İlk conflict item'ı otomatik seç
        if (action.payload?.conflictingItems && action.payload.conflictingItems.length > 0) {
          state.selectedConflictItemId = action.payload.conflictingItems[0].specificationItemId;
        } else {
          state.selectedConflictItemId = null;
        }
      })
      .addCase(getProductConflictsThunk.rejected, (state, action) => {
        state.productConflictsLoading = false;
        state.productConflictsError =
          (action.payload as string) || "Bir hata oluştu";
      })
      .addCase(createOrUpdateResolutionThunk.pending, (state) => {
        state.resolutionLoading = true;
        state.resolutionError = null;
      })
      .addCase(createOrUpdateResolutionThunk.fulfilled, (state, action) => {
        state.resolutionLoading = false;
        state.resolutionError = null;
        // Update the resolution in the conflicts data
        if (state.productConflicts && state.selectedConflictItemId) {
          const item = state.productConflicts.conflictingItems.find(
            (item) => item.specificationItemId === state.selectedConflictItemId
          );
          if (item) {
            item.resolution = action.payload;
          }
        }
        // Submit edilen item id'yi submittedConflictItemIds'e ekle
        if (state.selectedConflictItemId && !state.submittedConflictItemIds.includes(state.selectedConflictItemId)) {
          state.submittedConflictItemIds.push(state.selectedConflictItemId);
        }
      })
      .addCase(createOrUpdateResolutionThunk.rejected, (state, action) => {
        state.resolutionLoading = false;
        state.resolutionError =
          (action.payload as string) || "Bir hata oluştu";
      });
  },
});

export const {
  clearError,
  setActiveProductId,
  setCurrentSupplierCarouselIndex,
  toggleCategory,
  collapseAll,
  expandAll,
  toggleAll,
  setSelectedConflictItemId,
  resetConflictData,
} = supplierResponseSingleSlice.actions;

export default supplierResponseSingleSlice.reducer;
